/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useRequest } from '@axios-use/react';
import { useDeepCompareEffect } from 'ahooks';
import { useImmer } from 'use-immer';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';

import type {
  CartCustomization_,
  CartDetailResponse,
  CartInfoParams,
  MenuCartResponseProduct,
  MenuCartComboProduct_,
} from '@/apis/cart';
import type { PutUpdateCartBody } from '@/apis/product/index';
import type {
  ProductData,
  ProductInfo,
  ProductInfoComboItem,
  ProductInfoComboProductType,
  ProductInfoCustomization,
  ProductInfoCustomizationItem,
  ProductInfoCustomizationItemValueItem,
  ProductInfoCustomizationOption,
} from '@/apis/product/types';
import { getPOSTBogoPriceReduction } from '@/apis/product';
import { useCartAction, useCartState } from '@/hooks/useCart';

import { getCustomizationItemsTotalPrice, isDCSingleCombo } from './func';
import { checkSingleComboProductIsDefault } from '../utils';

/**
 * get extraCustomizationCache key
 */
export const getExtraCustomizationCacheKey = (
  combo: ProductInfoComboItem,
  producCode: string,
  /** other custom identify */
  identify?: string | number,
): string => {
  return `${combo.choicesCode || ''}#R${combo.round || ''}#I${combo.idx || ''}-${producCode}${
    identify != null ? '#' + String(identify) : ''
  }`;
};

/**
 *
 * @param product
 * @param extraCache
 */
export const getProductComboAndCustomizationDiffPrice = (
  product?: ProductInfo,
  extraCache?: Record<string, ProductInfoCustomization>,
) => {
  let _price = 0;

  if (product?.comboItems) {
    product.comboItems.forEach((c) => {
      if (c.comboProducts) {
        const _p = c.comboProducts.find((p) => checkSingleComboProductIsDefault(p));
        if (_p?.diffPrice) {
          _price += _p.diffPrice;
        }

        if (_p?.code && extraCache && extraCache[getExtraCustomizationCacheKey(c, _p.code)]) {
          const _extraCustomization = extraCache[getExtraCustomizationCacheKey(c, _p.code)];
          if (_extraCustomization?.items) {
            _price += getCustomizationItemsTotalPrice(_extraCustomization.items);
          }
        }
      }
    });
  }

  if (product?.customization?.items) {
    _price += getCustomizationItemsTotalPrice(product.customization.items);
  }

  return _price;
};

const fmtProductCustomizationToBody = (
  item?: ProductInfoCustomization | null,
): ProductInfoCustomization | undefined => {
  if (item) {
    const { items, options } = item;
    if (items && Array.isArray(items)) {
      const _items = items.map((i) => {
        const _vs = i.values?.filter((v) => v.checked === 1);
        return { ...i, values: _vs };
      });
      return { items: _items, options };
    }

    return { items: [], options };
  }
  return undefined;
};

/** for type=7 (DC) */
export const fmtDCProductComboItemsToBody = (
  combos?: ProductInfoComboItem[],
  extraCustomizationCache?: Record<string, ProductInfoCustomization>,
): ProductInfoComboItem[] | undefined => {
  if (combos && Array.isArray(combos) && !isEmpty(combos)) {
    return combos
      .map((combo) => {
        const { choicesCode, round, isChoices, comboProducts } = combo;
        const _isSingle = isDCSingleCombo(combo);
        const _hasActivedProducts = _isSingle
          ? comboProducts?.some((p) => checkSingleComboProductIsDefault(p))
          : comboProducts?.some((p) => p.quantity && p.quantity > 0);
        if (_hasActivedProducts) {
          const _filter = _isSingle
            ? comboProducts?.filter((p) => checkSingleComboProductIsDefault(p))
            : comboProducts?.filter((p) => p.quantity && p.quantity > 0);
          const _comboProducts = flatMap(_filter, (p) => {
            if (p.quantity && p.quantity > 1) {
              return Array.from({ length: p.quantity }, (_, i) => i).map((idx) => {
                const _customization = fmtProductCustomizationToBody(
                  (p.code && extraCustomizationCache?.[getExtraCustomizationCacheKey(combo, p.code, idx)]) ||
                    p.customization,
                );
                return {
                  name: p.name,
                  code: p.code,
                  quantity: 1,
                  customization: _customization,
                };
              });
            }
            const _cacheKey = p.code
              ? combo.isIncludeRound === 1 || _isSingle
                ? getExtraCustomizationCacheKey(combo, p.code)
                : getExtraCustomizationCacheKey(combo, p.code, 0)
              : undefined;
            const _customization = fmtProductCustomizationToBody(
              (_cacheKey && extraCustomizationCache?.[_cacheKey]) || p.customization,
            );
            return { name: p.name, code: p.code, quantity: 1, customization: _customization };
          });

          return {
            choicesCode,
            round,
            isChoices,
            comboProducts: _comboProducts,
          };
        }
        return undefined;
      })
      .filter(Boolean) as ProductInfoComboItem[];
  }
  return undefined;
};

export function fmtProductCacheToPutBody(
  quantity?: number,
  matchedSpecsCode?: string,
  body?: Omit<PutUpdateCartBody, 'products'>,
  productCache?: ProductData['product'],
  extraCustomizationCache?: Record<string, ProductInfoCustomization>,
  /** 编辑 FROM CART */
  fromCartPrevData?: MenuCartResponseProduct,
  /** for addToCart. coupon info */
  otherCouponInfo?: { couponId?: string; couponCode?: string; promotionId?: string },
): PutUpdateCartBody | undefined {
  if (quantity && body && productCache) {
    const cutProduct =
      productCache.type === 3
        ? productCache?.products?.find((p) => p.matchedSpecsCode === matchedSpecsCode)
        : productCache;

    const type = Number(cutProduct?.type);

    // TODO delete
    console.info(type, productCache, extraCustomizationCache);

    if (!cutProduct) {
      return undefined;
    }

    const fmtComboItems = (items?: ProductInfoComboItem[]): ProductInfoComboItem[] | undefined => {
      if (items && Array.isArray(items)) {
        return items.map((i) => {
          const _comboProducts = i.comboProducts
            ?.filter((p) => checkSingleComboProductIsDefault(p))
            ?.map((p) => {
              const _code = p.code;
              let customization = null;
              if (
                _code &&
                extraCustomizationCache &&
                extraCustomizationCache[getExtraCustomizationCacheKey(i, _code)]
              ) {
                customization = fmtProductCustomizationToBody(
                  extraCustomizationCache[getExtraCustomizationCacheKey(i, _code)],
                );
              }

              return { code: p.code, customization };
            });

          return { ...i, comboProducts: _comboProducts };
        });
      }

      return undefined;
    };

    const _replacedDataFromCart =
      fromCartPrevData && fromCartPrevData.quantity
        ? { ...fromCartPrevData, quantity: -fromCartPrevData.quantity }
        : undefined;
    const _couponInfoFromCart = _replacedDataFromCart?.coupon
      ? {
          couponId: _replacedDataFromCart.coupon?.id,
          couponCode: _replacedDataFromCart.coupon?.code,
          promotionId: _replacedDataFromCart.coupon?.promotionId,
        }
      : undefined;

    if (type === 1) {
      const { code, customization, name } = cutProduct;

      const _product = {
        code,
        customization: fmtProductCustomizationToBody(customization),
        name,
        quantity,
        type: 1,
      };

      return {
        ...body,
        products: _replacedDataFromCart
          ? [_replacedDataFromCart, { ..._product, ..._couponInfoFromCart }]
          : [{ ..._product, ...otherCouponInfo }],
      } as PutUpdateCartBody;
    }

    if (type === 2) {
      const { code, customization, name, comboItems } = cutProduct;
      const _product = {
        code,
        customization: fmtProductCustomizationToBody(customization),
        comboItems: fmtComboItems(comboItems),
        name,
        quantity,
        type: 2,
      };

      return {
        ...body,
        products: _replacedDataFromCart
          ? [_replacedDataFromCart, { ..._product, ..._couponInfoFromCart }]
          : [{ ..._product, ...otherCouponInfo }],
      } as PutUpdateCartBody;
    }

    if (type === 7) {
      const { code, name, comboItems } = cutProduct;

      const _product = {
        code,
        comboItems: fmtDCProductComboItemsToBody(comboItems, extraCustomizationCache),
        name,
        quantity,
        type: 7,
      };

      return {
        ...body,
        products: _replacedDataFromCart
          ? [_replacedDataFromCart, { ..._product, ..._couponInfoFromCart }]
          : [{ ..._product, ...otherCouponInfo }],
      } as PutUpdateCartBody;
    }
  }
  return undefined;
}

function parseProductCustomizationCartToCache(customization: CartCustomization_): ProductInfoCustomization {
  const { items, options } = customization;

  const _items = items?.map((i) => {
    return { ...i, values: i.values?.map((v) => ({ ...v, checked: 1 })) };
  });

  return { items: _items, options } as ProductInfoCustomization;
}

/**
 * 1. Filter out sold out products
 */
export function fmtProductCache(pCache?: ProductData['product']): ProductData['product'] | undefined {
  if (pCache) {
    // filter sellout
    let _rtnPData = { ...pCache };

    const _filterSelloutComboProducts = (cps?: ProductInfoComboItem[]): ProductInfoComboItem[] | undefined => {
      if (cps) {
        if (
          cps.some((c) =>
            c?.comboProducts?.some(
              (p) => p.sellout === true && (checkSingleComboProductIsDefault(p) || (p.quantity || 0) > 0),
            ),
          )
        ) {
          return cps.map((cp) => ({
            ...cp,
            comboProducts: cp.comboProducts?.map((p) => {
              if (p?.sellout === true) {
                const _p = { ...p };
                if (_p.isDefault === 1) {
                  _p.isDefault = 0;
                }
                if (typeof _p.quantity === 'number' && _p.quantity > 0) {
                  _p.quantity = 0;
                }
                return _p;
              }
              return p;
            }),
          }));
        }
      }
      return cps;
    };
    // product.comboItems.comboProducts.sellout
    if (pCache.comboItems) {
      const _comboItems = _filterSelloutComboProducts(pCache.comboItems);
      _rtnPData = { ..._rtnPData, comboItems: _comboItems };
    }
    // product.products.comboItems.comboProducts.sellout
    if (pCache.products) {
      const _products = pCache.products.map((p) =>
        p.comboItems ? { ...p, comboItems: _filterSelloutComboProducts(p.comboItems) } : p,
      );
      _rtnPData = { ..._rtnPData, products: _products };
    }

    return _rtnPData;
  }
  return pCache;
}

export function parseProductCartToCache(
  pCache?: ProductData['product'],
  cartProduct?: MenuCartResponseProduct,
): [ProductData['product'] | undefined, Record<string, ProductInfoCustomization>] {
  if (pCache && cartProduct) {
    const { customization, comboItems } = cartProduct;
    let _rtnPData = { ...pCache };
    const _extraCustomizationCache: Record<string, ProductInfoCustomization> = {};

    if (pCache.customization && customization) {
      const { items, options } = pCache.customization;

      const _items = items?.map((i) => {
        const _i = customization.items?.find((ii) => ii.code === i.code);
        const _vs = i.values?.map((v) => {
          const _checkedValue = _i?.values?.[0];
          return { ...v, checked: _checkedValue?.code === v.code ? 1 : 0 };
        });
        return { ...i, values: _vs };
      });

      const _options = options?.map((o) => {
        const _o = customization.options?.find((oo) => oo.code === o.code);
        if (_o) {
          return { ...o, checked: _o.checked === 1 ? 1 : 0 };
        }
        return o;
      });

      if (items || options) {
        _rtnPData = { ..._rtnPData, customization: { items: _items, options: _options } };
      }
    }

    if (pCache.comboItems && comboItems) {
      let _comboItems: ProductInfoComboItem[] = [];
      if (pCache.type === 7) {
        _comboItems = pCache.comboItems.map((c) => {
          const _cartComboProducts = comboItems.find((c2) => c2.round === c.round)
            ?.comboProducts as MenuCartComboProduct_[];
          const _cartComboProductCodes = _cartComboProducts?.map((p) => p.code).filter(Boolean) as string[];

          if (_cartComboProducts && Array.isArray(_cartComboProducts) && _cartComboProducts.length > 0) {
            const _isSingle = isDCSingleCombo(c);
            const _cartComboProductsGroup = groupBy(_cartComboProducts, (cp) => cp.code);
            Object.keys(_cartComboProductsGroup).forEach((pcode) => {
              const _arr = _cartComboProductsGroup[pcode];
              if (!isEmpty(_arr)) {
                if (_arr.length > 1) {
                  let _index = 0;
                  _arr.forEach((cp) => {
                    if (cp.customization) {
                      if (cp.quantity === 1) {
                        const _cacheKey = getExtraCustomizationCacheKey(c, pcode, _index);
                        _extraCustomizationCache[_cacheKey] = parseProductCustomizationCartToCache(cp.customization);
                        _index += 1;
                      } else if (cp.quantity && cp.quantity > 1) {
                        const _data = parseProductCustomizationCartToCache(cp.customization);
                        Array.from({ length: cp.quantity }).forEach(() => {
                          const _cacheKey = getExtraCustomizationCacheKey(c, pcode, _index);
                          _extraCustomizationCache[_cacheKey] = _data;
                          _index += 1;
                        });
                      }
                    }
                  });
                } else if (
                  _arr[0] &&
                  _arr[0].customization &&
                  (!isEmpty(_arr[0].customization.items) || !isEmpty(_arr[0].customization.options))
                ) {
                  const _quantity = _arr[0].quantity;
                  if (_quantity === 1) {
                    const _cacheKey =
                      c.isIncludeRound === 1 || _isSingle
                        ? getExtraCustomizationCacheKey(c, pcode)
                        : getExtraCustomizationCacheKey(c, pcode, 0);
                    _extraCustomizationCache[_cacheKey] = parseProductCustomizationCartToCache(_arr[0].customization);
                  } else if (_quantity && _quantity > 1) {
                    const _data = parseProductCustomizationCartToCache(_arr[0].customization);
                    Array.from({ length: _quantity }, (_, i) => i).forEach((idx) => {
                      const _key = getExtraCustomizationCacheKey(c, pcode, idx);
                      _extraCustomizationCache[_key] = _data;
                    });
                  }
                }
              }
            });

            const _comboProducts = c.comboProducts?.map((p) => {
              if (_isSingle) {
                const _checked = p.code && p.sellout !== true && _cartComboProductCodes.includes(p.code);
                return { ...p, isDefault: _checked ? 1 : 0, quantity: _checked ? 1 : 0 };
              } else {
                const _quantity = _cartComboProducts
                  .filter((cp) => cp.code && cp.code === p.code && p.sellout !== true)
                  .map((cp) => cp.quantity || 0)
                  .reduce((a, b) => a + b, 0);

                return { ...p, quantity: _quantity ?? 0 };
              }
            });

            return { ...c, comboProducts: _comboProducts };
          } else {
            // clear quantity
            const _comboProducts = c.comboProducts?.map((p) => ({ ...p, quantity: 0 }));
            return { ...c, quantity: 0, comboProducts: _comboProducts };
          }
        });
      } else {
        _comboItems = pCache.comboItems.map((c, index) => {
          const _cartProduct = comboItems[index]?.comboProducts?.[0];
          const _defaultPCode = _cartProduct?.code;

          if (_defaultPCode) {
            if (_cartProduct?.customization) {
              _extraCustomizationCache[getExtraCustomizationCacheKey(c, _defaultPCode)] =
                parseProductCustomizationCartToCache(_cartProduct.customization);
            }
            const _comboProducts = c.comboProducts?.map((p) => {
              const _checked = p.sellout !== true && p.code === _defaultPCode;
              return { ...p, isDefault: _checked ? 1 : 0, quantity: _checked ? 1 : 0 };
            });
            return { ...c, comboProducts: _comboProducts };
          }

          return c;
        });
      }

      if (_comboItems && _comboItems.length > 0) {
        _rtnPData = { ..._rtnPData, comboItems: _comboItems };
      }
    }

    return [_rtnPData, _extraCustomizationCache];
  }

  return [pCache, {}];
}

function useGetProductForCart(productCode?: string, options?: { sequence?: number } & Partial<CartInfoParams>) {
  const [curCartProduct, setCurCartProduct] = useState<MenuCartResponseProduct>();
  const [cartstate, cartstateStatus] = useCartState(options?.orderType);
  const { syncTheLatest } = useCartAction();

  const findCartProduct = useCallback(
    (products?: MenuCartResponseProduct[]) =>
      (products || cartstate?.value?.products)?.find((p) => p.code === productCode && p.sequence === options?.sequence),
    [cartstate?.value?.products, options?.sequence, productCode],
  );
  const findCartProductRef = useRef(findCartProduct);
  findCartProductRef.current = findCartProduct;

  useDeepCompareEffect(() => {
    // 购物车编辑模式，无购物车数据时需要重新请求
    if (!cartstate?.value && cartstateStatus === 'idle' && options && productCode) {
      const { cartType, daypartCode, orderType, storeCode, sequence, ...restParams } = options;

      if (cartType && daypartCode && orderType && storeCode) {
        void syncTheLatest({
          cartType,
          daypartCode,
          orderType,
          storeCode,
          ...restParams,
        }).then((res) => {
          const _p = findCartProduct((res.payload as CartDetailResponse)?.products);
          setCurCartProduct(_p);
        });
      }
    }
  }, [options, cartstate, cartstateStatus, productCode, syncTheLatest]);

  useEffect(() => {
    const _p = findCartProductRef.current();
    setCurCartProduct(_p);
  }, [options?.sequence, productCode]);

  return curCartProduct;
}

export default function useProductDetailEditorCache(
  data?: ProductData['product'],
  fromCart?: { sequence?: number } & Partial<CartInfoParams>,
  otherCouponInfo?: { couponId?: string; couponCode?: string; promotionId?: string },
) {
  const isFromCart = useMemo(() => Boolean(fromCart), [fromCart]);
  const [productCache, updateProductCache] = useImmer(isFromCart ? undefined : data);

  // TODO 未与 matchedSpecsCode 进行绑定, 切换套餐会继承相同产品定制
  const [extraCustomizationCache, updateExtraCustomizationCache] = useImmer<Record<string, ProductInfoCustomization>>(
    {},
  );

  const { updateCart } = useCartAction();
  const [createPOSTBogoPriceReductionReq, postBogoPriceReductionState] = useRequest(getPOSTBogoPriceReduction);

  const restProductCache = useCallback(() => {
    updateProductCache(fmtProductCache(data));
    updateExtraCustomizationCache({});
  }, [data, updateExtraCustomizationCache, updateProductCache]);

  useDeepCompareEffect(() => {
    if (!isFromCart) {
      updateProductCache(fmtProductCache(data));
    }
  }, [data, isFromCart]);

  // for fromCart: start-------------
  const curCartProduct = useGetProductForCart(data?.code, fromCart);
  const disabledCountStepper = useMemo(
    () =>
      Boolean(
        curCartProduct?.coupon &&
          Number(curCartProduct.coupon.showPmtType) === 1 &&
          Number(curCartProduct.coupon.rightCardType !== 3),
      ),
    [curCartProduct?.coupon],
  );
  const [initProductForCart, setInitProductForCart] = useState<{
    value?: ProductData['product'];
    extra?: Record<string, ProductInfoCustomization>;
  }>();
  useDeepCompareEffect(() => {
    if (isFromCart && fromCart?.sequence && curCartProduct) {
      const [_c, _e] = parseProductCartToCache(data, curCartProduct);
      updateProductCache(_c);
      updateExtraCustomizationCache(_e);
      setInitProductForCart({ value: _c, extra: _e });
    }
  }, [fromCart?.sequence, isFromCart, data, curCartProduct]);
  // ---------------end fromCart

  // 多选
  const changeCustomizationOptionCheck = useCallback(
    (check: number, productCode?: string, option?: ProductInfoCustomizationOption) => {
      updateProductCache((d) => {
        if (d?.code === productCode) {
          const _index = d?.customization?.options?.findIndex((o) => o.code === option?.code);
          if (_index != null && d?.customization?.options) {
            d.customization.options[_index].checked = check;
          }
        } else if (d?.products) {
          const _pIdx = d.products.findIndex((p) => p.code === productCode);
          if (_pIdx != null) {
            const _oIdx = d.products[_pIdx].customization?.options?.findIndex((o) => o.code === option?.code);
            if (_oIdx && d.products[_pIdx].customization?.options) {
              d.products[_pIdx].customization!.options[_oIdx].checked = check;
            }
          }
        }
      });
    },
    [updateProductCache],
  );

  // 单选
  const changeCustomizationItemCheck = useCallback(
    (productCode?: string, item?: ProductInfoCustomizationItem, itemValue?: ProductInfoCustomizationItemValueItem) => {
      updateProductCache((d) => {
        if (d?.code === productCode) {
          const _iIdx = d?.customization?.items.findIndex((i) => i.code === item?.code);
          if (
            _iIdx != null &&
            d?.customization?.items[_iIdx].values?.find((v) => v?.code && itemValue?.code && v.code === itemValue.code)
          ) {
            const _values = d.customization.items[_iIdx].values?.map((v) => {
              if (v.code === itemValue?.code) {
                return { ...v, checked: 1 };
              }
              return { ...v, checked: 0 };
            });
            d.customization.items[_iIdx].values = _values;
          }
        } else if (d?.products) {
          const _pIdx = d.products.findIndex((p) => p.code === productCode);
          if (_pIdx != null) {
            const _iIdx = d.products[_pIdx].customization?.items.findIndex((i) => i.code === item?.code);
            if (
              _iIdx != null &&
              d.products[_pIdx].customization!.items[_iIdx].values?.find((v) => v?.code === itemValue?.code)
            ) {
              const _values = d.products[_pIdx].customization!.items[_iIdx].values?.map((v) => {
                if (v.code === itemValue?.code) {
                  return { ...v, checked: 1 };
                }
                return { ...v, checked: 0 };
              });

              d.products[_pIdx].customization!.items[_iIdx].values = _values;
            }
          }
        }
      });
    },
    [updateProductCache],
  );

  const changeComboItemsProductDefault = useCallback(
    (productCode?: string, combo?: ProductInfoComboItem, comboProduct?: ProductInfoComboProductType) => {
      if (productCode && combo && comboProduct) {
        updateProductCache((d) => {
          if (d?.code === productCode) {
            const _cIndex = d.comboItems?.findIndex((c) =>
              d.type === 7 ? c.round === combo.round : c.idx === combo.idx,
            );

            if (_cIndex != null) {
              const _comboProducts = d.comboItems![_cIndex].comboProducts?.map((p) => {
                if (p.code === comboProduct.code) {
                  return { ...p, isDefault: 1, quantity: 1 };
                }
                return { ...p, isDefault: 0, quantity: 0 };
              });

              d.comboItems![_cIndex].comboProducts = _comboProducts;
            }
          } else if (d?.products) {
            const _pIdx = d.products.findIndex((p) => p.code === productCode);
            if (_pIdx != null) {
              const _curp = d.products[_pIdx];
              const _cIndex = _curp.comboItems?.findIndex((c) =>
                _curp.type === 7 ? c.round === combo.round : c.idx === combo.idx,
              );
              if (_cIndex != null) {
                const _comboProducts = d.products[_pIdx].comboItems![_cIndex].comboProducts?.map((p) => {
                  if (p.code === comboProduct.code) {
                    return { ...p, isDefault: 1, quantity: 1 };
                  }
                  return { ...p, isDefault: 0, quantity: 0 };
                });

                d.products[_pIdx].comboItems![_cIndex].comboProducts = _comboProducts;
              }
            }
          }
        });
      }
    },
    [updateProductCache],
  );

  const changeComboItemsProductMutliCount = useCallback(
    (productCode?: string, combo?: ProductInfoComboItem, comboProduct?: ProductInfoComboProductType, diff?: number) => {
      if (productCode && combo && comboProduct && diff != null) {
        updateProductCache((d) => {
          if (d?.code === productCode) {
            const _cIndex = d.comboItems?.findIndex((c) => c.round === combo.round);
            if (_cIndex != null) {
              const _comboProducts = d.comboItems![_cIndex].comboProducts?.map((p) => {
                if (p.code === comboProduct.code) {
                  return { ...p, quantity: (p.quantity || 0) + diff };
                }
                return p;
              });
              d.comboItems![_cIndex].comboProducts = _comboProducts;
              const _quantity = d.comboItems![_cIndex].quantity || 0;
              d.comboItems![_cIndex].quantity = _quantity + diff;
            }
          } else if (d?.products) {
            const _pIdx = d.products.findIndex((p) => p.code === productCode);
            if (_pIdx != null) {
              const _cIndex = d.products[_pIdx].comboItems?.findIndex((c) => c.round === combo.round);
              if (_cIndex != null) {
                const _comboProducts = d.products[_pIdx].comboItems![_cIndex].comboProducts?.map((p) => {
                  if (p.code === comboProduct.code) {
                    return { ...p, quantity: (p.quantity || 0) + diff };
                  }
                  return p;
                });

                d.products[_pIdx].comboItems![_cIndex].comboProducts = _comboProducts;
                const _quantity = d.products[_pIdx].comboItems![_cIndex].quantity || 0;
                d.products[_pIdx].comboItems![_cIndex].quantity = _quantity + diff;
              }
            }
          }
        });
      }
    },
    [updateProductCache],
  );

  const addToCart = useCallback(
    async (quantity?: number, matchedSpecsCode?: string, otherBody?: Omit<PutUpdateCartBody, 'products'>) => {
      const body = fmtProductCacheToPutBody(
        quantity,
        matchedSpecsCode,
        otherBody,
        productCache,
        extraCustomizationCache,
        undefined,
        otherCouponInfo,
      );

      if (!body) {
        throw new Error('加入购物车失败');
      }

      return await updateCart(body);
    },
    [extraCustomizationCache, otherCouponInfo, productCache, updateCart],
  );

  const saveToCart = useCallback(
    async (quantity?: number, matchedSpecsCode?: string, otherBody?: Omit<PutUpdateCartBody, 'products'>) => {
      const body = fmtProductCacheToPutBody(
        quantity,
        matchedSpecsCode,
        otherBody,
        productCache,
        extraCustomizationCache,
        curCartProduct,
      );

      if (!body) {
        throw new Error('保存失败');
      }

      return await updateCart(body);
    },
    [curCartProduct, extraCustomizationCache, productCache, updateCart],
  );

  return {
    productCache,
    updateProductCache,
    extraCustomizationCache,
    updateExtraCustomizationCache,
    /** fromCart */
    initCountFromCart: curCartProduct?.quantity,
    /** fromCart */
    disabledCountStepper,
    /** fromCart */
    curCartProduct,
    /** fromCart */
    initProductForCart,
    changeCustomizationOptionCheck,
    changeCustomizationItemCheck,
    changeComboItemsProductDefault,
    changeComboItemsProductMutliCount,
    restProductCache,
    addToCart,
    saveToCart,

    // DC smart price
    createPOSTBogoPriceReductionReq,
    postBogoPriceReductionState,
  };
}
